/**
 * Yjs document actions for `sentiment` questions.
 *
 * A sentiment scale stores its options in an ordinary likert answer set, so
 * these actions are thin wrappers over the generic answer-set actions. What they
 * add is the invariant the type depends on: for a set the author is editing
 * inline, values run `0..N-1` ascending in display order. Today's live
 * assessment data is valued `"0"`/`"1"`/`"2"`, so it round-trips unchanged.
 *
 * Borrowed sets are left alone. `Frequency` is valued `1..5` and renumbering it
 * would corrupt a set that other questions may share.
 */

import { Map as YMap } from 'yjs';
import Big from 'big.js';
import { randomBase32String, standardCollator } from 'js/utils/string';
import {
  DEFAULT_DECORATION,
  MIN_SENTIMENT_OPTIONS,
  MAX_SENTIMENT_OPTIONS,
  SENTIMENT_TYPE,
} from 'js/utils/sentiment-question';
import { buildPositionMap } from 'js/ydoc/docs/common/utils';
import {
  moveAnswerSetChoiceAfter,
  moveAnswerSetChoiceBefore,
} from './answer-set-actions';
import { calcPositionBetween } from './utils';

const RANDOM_ID_STRING_LENGTH = 10;

/** Matches the shape of every live assessment in the data today. */
const DEFAULT_OPTION_TEXT = Object.freeze([
  'Needs work',
  'Getting there',
  'Going well',
]);

function newGuid(prefix) {
  return `${prefix}-${randomBase32String(RANDOM_ID_STRING_LENGTH)}`;
}

function orderedChoices(choicesMap) {
  return [...choicesMap.values()].sort((a, b) =>
    standardCollator.compare(a.get('p'), b.get('p'))
  );
}

/**
 * Renumbers a set's values to `0..N-1` in display order. Called after every
 * add, remove and reorder so position and value never drift apart.
 */
export function reassignSentimentValues(choicesMap) {
  orderedChoices(choicesMap).forEach((choice, index) => {
    const value = String(index);
    if (choice.get('value') !== value) {
      choice.set('value', value);
    }
  });
}

/**
 * Renumbers only a set the author owns.
 *
 * A standard set is shared: `Frequency` is valued `1..5`, and rewriting that to
 * `0..4` would change the meaning of every other question that borrowed it, and
 * of the responses already recorded against those values.
 */
function renumberIfInline(answerSet, choicesMap) {
  if (answerSet?.get('isStandard')) {
    return;
  }

  reassignSentimentValues(choicesMap);
}

function buildChoices(texts) {
  return new YMap(
    texts.map((text, index) => {
      const guid = newGuid('c');
      return [
        guid,
        new YMap(
          Object.entries({
            guid,
            value: String(index),
            text,
            p: String((index + 1) / (texts.length + 1)),
          })
        ),
      ];
    })
  );
}

/**
 * Adds a sentiment question seeded with its own custom answer set. New
 * questions get a custom set rather than a standard one because the option text
 * is the substance of the question, not a reusable scale.
 */
export function addSentimentQuestion(doc, questionSetId, initialText) {
  const guid = randomBase32String(RANDOM_ID_STRING_LENGTH);
  const answerSetGuid = newGuid('as');

  doc.transact(() => {
    doc.getMap('answerSets').set(
      answerSetGuid,
      new YMap(
        Object.entries({
          guid: answerSetGuid,
          name: null,
          type: 'likert',
          isStandard: false,
          isPublished: false,
          choices: buildChoices(DEFAULT_OPTION_TEXT),
        })
      )
    );

    const question = new YMap();
    question.set('elementType', 'question');
    question.set('guid', guid);
    question.set('key', `q-${guid}`);
    question.set('text', initialText);
    question.set('type', SENTIMENT_TYPE);
    question.set('decoration', DEFAULT_DECORATION);
    question.set(
      'config',
      new YMap(Object.entries({ decoration: DEFAULT_DECORATION }))
    );
    question.set('answerSetId', answerSetGuid);
    question.set(
      'position',
      buildPositionMap(doc.getMap('elements'), 'pageElements', questionSetId)
    );

    doc.getMap('elements').set(guid, question);
  });

  return guid;
}

export function setQuestionDecoration(doc, questionId, decoration) {
  const question = doc.getMap('elements').get(questionId);
  if (!(question instanceof YMap)) {
    return;
  }

  question.set('decoration', decoration);

  const existing = question.get('config');
  if (existing instanceof YMap) {
    existing.set('decoration', decoration);
    return;
  }

  question.set('config', new YMap(Object.entries({ decoration })));
}

/**
 * Appends an option at the top of the scale. Refuses past
 * `MAX_SENTIMENT_OPTIONS` so the button can stay enabled-looking while the
 * action itself is the real guard.
 */
export function addSentimentOption(doc, answerSetId, props = {}) {
  doc.transact(() => {
    const answerSet = doc.getMap('answerSets').get(answerSetId);
    const choices = answerSet?.get('choices');
    if (choices == null || choices.size >= MAX_SENTIMENT_OPTIONS) {
      return;
    }

    const last = orderedChoices(choices).at(-1);
    const guid = newGuid('c');

    choices.set(
      guid,
      new YMap(
        Object.entries({
          text: `Option ${choices.size + 1}`,
          ...props,
          guid,
          p: calcPositionBetween(Big(last?.get('p') ?? 0), Big(1)).toString(),
          value: String(choices.size),
        })
      )
    );

    renumberIfInline(answerSet, choices);
  });
}

/**
 * Reorders an option, dropping it before or after `targetChoiceId`.
 *
 * The move and the renumber happen in one transaction because on a sentiment
 * scale the position *is* the value: an observer that saw the two steps apart
 * would briefly render a scale whose decorations disagreed with its values.
 *
 * A shared set is refused outright. Reordering it here would silently reorder
 * it for every other question that borrowed it, and renumbering would rewrite
 * values those questions' existing responses are recorded against.
 */
export function moveSentimentOption(
  doc,
  answerSetId,
  sourceChoiceId,
  targetChoiceId,
  placeAfter = false
) {
  doc.transact(() => {
    const answerSet = doc.getMap('answerSets').get(answerSetId);
    const choices = answerSet?.get('choices');

    if (choices == null || answerSet.get('isStandard')) {
      return;
    }

    const move = placeAfter
      ? moveAnswerSetChoiceAfter
      : moveAnswerSetChoiceBefore;

    move(doc, answerSetId, sourceChoiceId, targetChoiceId);
    renumberIfInline(answerSet, choices);
  });
}

/** Whether this question's options may be reordered inline. */
export function canReorderSentimentOptions(answerSet) {
  return !!answerSet && !answerSet.isStandard;
}

/** Removes an option, refusing to drop below `MIN_SENTIMENT_OPTIONS`. */
export function deleteSentimentOption(doc, answerSetId, choiceId) {
  doc.transact(() => {
    const choices = doc.getMap('answerSets').get(answerSetId)?.get('choices');
    if (choices == null || choices.size <= MIN_SENTIMENT_OPTIONS) {
      return;
    }

    choices.delete(choiceId);
    reassignSentimentValues(choices);
  });
}

export function canAddSentimentOption(answerSet) {
  return choiceCount(answerSet) < MAX_SENTIMENT_OPTIONS;
}

export function canRemoveSentimentOption(answerSet) {
  return choiceCount(answerSet) > MIN_SENTIMENT_OPTIONS;
}

function choiceCount(answerSet) {
  return Object.keys(answerSet?.choices ?? {}).length;
}
